import React, { useEffect, useCallback, useRef, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { ChatInput, CustomButton, colors } from "@ippon/shared-components";
import { SIDEBAR_TRANSITION_TIMING } from "@ippon/shared-layouts";
import { formatTheme } from "@/theme/formatTheme";
import { useAppDispatch } from "@/store/hooks";
import { requestChatOpen, warmupAgent } from "@/store/chatSlice";
import { setSidebarCollapsed } from "@/store/sidebarSlice";
import { HomeChatHistory, HOME_HISTORY_PANEL_WIDTH } from "./HomeChatHistory";

// ヘッダー(50px)を差し引いた表示領域の高さ。あいさつ+入力を縦中央に置く。
const CONTENT_HEIGHT = "calc(100vh - 80px)";

/**
 * ホーム = チャットのランチャー。
 * あいさつ + 入力だけを中央に置き、送信するとフローティングチャット(AIChatButton)を
 * 全画面(大画面)で開いて、その文言をそのまま送信する。会話ビューは Home 内に持たない。
 * 左上の「履歴」で過去の会話一覧を出し、選ぶと全画面チャットでその会話を開く。
 */
const HomePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  // 入力欄の位置を、開くアニメーションの起点として渡すための ref。
  const inputRef = useRef<HTMLDivElement>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // 入室時にエージェントをウォームアップ（初回送信の待ち時間を短縮）。
  useEffect(() => {
    void dispatch(warmupAgent());
  }, [dispatch]);

  // 履歴を開いている間は左サイドバーを縮小し、閉じる・画面を離れるときに元へ戻す。
  useEffect(() => {
    if (!isHistoryOpen) return;

    dispatch(setSidebarCollapsed(true));

    return () => {
      dispatch(setSidebarCollapsed(false));
    };
  }, [isHistoryOpen, dispatch]);

  // 送信 → 大画面チャットを、入力位置を起点に広げて開き、即送信（ランチャー）。
  const handleSendMessage = useCallback(
    (content: string) => {
      const q = content.trim();
      if (!q) return;
      const rect = inputRef.current?.getBoundingClientRect();
      const origin = rect
        ? { originX: rect.left + rect.width / 2, originY: rect.top + rect.height / 2 }
        : {};
      dispatch(requestChatOpen({ sendQuery: q, expand: true, ...origin }));
    },
    [dispatch]
  );

  // 履歴の会話 → 押した位置を起点に、全画面チャットでその会話を開く。履歴は開いたまま残す。
  const handleSelectThread = useCallback(
    (threadId: string, origin: { x: number; y: number }) => {
      dispatch(
        requestChatOpen({
          threadId,
          expand: true,
          originX: origin.x,
          originY: origin.y,
        })
      );
    },
    [dispatch]
  );

  return (
    <Box
      sx={{
        position: "relative",
        boxSizing: "border-box",
        minHeight: CONTENT_HEIGHT,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        // 900px 未満は入力を全幅にするため横パディングを外す
        // （あいさつ文だけ下で余白を戻す）。
        px: { xs: 0, md: 3 },
        // 履歴パネルの分だけ右へ寄せ、あいさつと入力を残りの領域の中央に保つ。
        // 600px 未満はパネルが全幅で重なるので寄せない。
        ml: { xs: 0, sm: isHistoryOpen ? `${HOME_HISTORY_PANEL_WIDTH}px` : 0 },
        transition: `margin-left ${SIDEBAR_TRANSITION_TIMING}`,
      }}
    >
      <HomeChatHistory
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        onSelectThread={handleSelectThread}
      />
      <Box
        data-testid="home-history-launcher"
        sx={{
          position: "absolute",
          top: 8,
          left: 0,
          opacity: isHistoryOpen ? 0 : 1,
          visibility: isHistoryOpen ? "hidden" : "visible",
          transition: `opacity ${SIDEBAR_TRANSITION_TIMING}, visibility 0s linear ${isHistoryOpen ? "0.2s" : "0s"}`,
        }}
      >
        <CustomButton
          label="履歴"
          buttonCategory="clear"
          clearIconAlwaysVisible
          startIcon={<HistoryIcon sx={{ fontSize: 18 }} />}
          onClick={() => setIsHistoryOpen(true)}
          sx={{ color: colors.text.primary }}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          // 900px 未満は上限なし(入力を全幅寄りに)、900px 以上は 720 で中央寄せ。
          maxWidth: { xs: "none", md: 720 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* あいさつ（入力が全幅でも文字が端に付かないよう余白を戻す） */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 3,
            px: { xs: 2, md: 0 },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, #5CC8E0 100%)`,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(11, 157, 189, 0.25)",
            }}
          >
            <AutoAwesomeOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            sx={{
              fontSize: { xs: 20, sm: 26 },
              fontWeight: 700,
              color: colors.text.primary,
              letterSpacing: "0.01em",
            }}
          >
            何かお手伝いできることはありますか？
          </Typography>
        </Box>

        {/* 中央の入力。900px 未満は共通レイアウトの横パディングを
            負マージンで一部打ち消して全幅寄りにする。 */}
        <Box
          ref={inputRef}
          sx={{
            // 親幅にきっちり伸ばし、負マージンで左右対称に外へ出す。
            alignSelf: "stretch",
            mx: { xs: -1, md: 0 },
          }}
        >
          <ChatInput
            onSendMessage={handleSendMessage}
            primaryColor={formatTheme.colors.primary.main}
            isExpanded={!isMobile}
            placeholder="どのようなお手伝いをさせていただけますか？"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HomePage;
